// The Briefing's research-pack filter (busy-my-web#56).
//
// Clicking "N Research Packs" in the Briefing narrows the story to its packs; the strip then LISTS
// them, each with an "open" line under it. From there the pack name is a SELECTION (it filters the
// middle to that pack and nothing else moves — the Briefing stays pinned at the top), while "open"
// is navigation (?pack=… — the pack itself takes the middle).
//
// Saffet: "When you select the research pack from the Briefing the follow-ups should disappear and
// also the compare research pack should be under the briefing. The Briefing always stays at the top
// then what you select filters in the middle."
//
// Both consequences of a selection — Follow-ups stepping aside, the compare ask moving under the
// Briefing — are the SAME question asked twice, so it is answered once here, as a plain function
// over `ongoing/storyFilters`. Same spirit as `isSearchOpen` in lib/searchMode.js: one rule, no
// store, testable without mounting anything.

import { scheduleText } from '@/lib/timelineTime'

/** Is a Research Pack selected in the Briefing (as opposed to merely filtering to "packs")? */
export const isPackSelected = (filters) => !!(filters && filters.packId)

/**
 * Is the Briefing's research-pack filter on at all — filtering to "packs" OR with one selected?
 *
 * Follow-ups and Focus step aside already at this point, not only once a pack is picked
 * (Pouria, 2026-08-16): filtering to the packs is entering that reading mode, and the life
 * overview underneath is noise for the whole of it. A selected pack keeps `kind` moving
 * (task/note narrow it further), so the packId alone must also count as "on".
 */
export const isPackFilterOn = (filters) => !!(filters && (filters.kind === 'research' || filters.packId))

/**
 * "Created today, 4pm" — the one fact an opened pack states about itself before its contents
 * (frame 40007883:371495).
 *
 * Rendered by the page, never written into the pack's document: the document is the user's to edit
 * and is persisted as it stands, so a stamp baked into its HTML would be editable, duplicable and
 * wrong the moment it was copied.
 *
 * The day reads lower case in the frame ("today"), where the shared `scheduleText` capitalises it
 * for the timeline rows that lead with it — so only the leading day word is lowered, and a dated
 * one ("23 Jul") is left exactly as the shared helper wrote it.
 */
export function packCreatedLine(pack, now = Date.now()) {
  const text = scheduleText(pack?.createdDate, now)
  return text ? `Created ${text.replace(/^(Today|Tomorrow|Yesterday)/, w => w.toLowerCase())}` : ''
}

// How many packs the strip shows before it scrolls. An ongoing can hold twenty and the Briefing is
// a STRIP — past a handful it stops being a summary and becomes the middle column. Four matches the
// save card's rationale in #53 (PACK_ROW_CAP = 3 + the recommended one): a short list you can take
// in at a glance, with the rest one scroll away rather than hidden behind a "more" affordance.
export const PACK_LIST_CAP = 4
// One entry is a single row now (h-5 = 20px) — the "open" action sits pinned at the row's end
// rather than on a line beneath (Pouria, 2026-08-15) — and the entries are stacked at 6px. Kept
// as numbers so the max-height is derived from the cap rather than hand-tuned twice.
const ENTRY_PX = 20
const GAP_PX = 6

/**
 * The pack list under the Briefing's "N Research Packs" row.
 *
 * @param packs   the ongoing's packs (`story.packs`)
 * @param selectedId `storyFilters.packId` — the one whose contents the middle is showing
 * @param cap     how many fit before the list scrolls
 * @returns `{ items: [{ id, title, active }], scroll, maxHeight }` — a plain value, so the whole
 *          briefing stays something the store can hand around (see lib/briefings/rows.js).
 */
export function packListView(packs, selectedId = null, cap = PACK_LIST_CAP) {
  // Newest first, the same order the middle's cards are in (busy-my-web#532): the strip and the
  // cards are two views of one list, and a pack that is top of one and bottom (or under the scroll)
  // of the other is two different answers to "which is the new one".
  const items = packsNewestFirst(packs)
    .filter(p => p && p.id)
    .map(p => ({ id: String(p.id), title: String(p.title || 'Research Pack'), active: String(p.id) === String(selectedId ?? '') }))

  const scroll = items.length > cap
  return {
    items,
    scroll,
    // Only a scrolling list needs a height: an uncapped one is exactly as tall as it is.
    maxHeight: scroll ? `${cap * ENTRY_PX + (cap - 1) * GAP_PX}px` : null,
  }
}

/**
 * THE PACK LIST, NEWEST FIRST (busy-my-web#532).
 *
 * Saffet, having just made one: "I should see the research pack listed in the middle top, so if i
 * just created a research pack and clicked the breadcrumbs back to research pack this should show
 * the research pack" — the pack he has this second finished making must be the first card he sees,
 * not the last one down a column of older ones.
 *
 * The server hands the packs over in the order it stored them (oldest first), and both surfaces
 * that list them — the middle's cards and the Briefing's strip — drew them in exactly that order,
 * so the newest pack was at the BOTTOM and, in a story with four or more, under the strip's own
 * scroll (`PACK_LIST_CAP`).
 *
 * Ordered on `createdDate`, the one instant every pack carries (the same field `packCreatedLine`
 * states). A pack with no date keeps its place relative to the ones around it — `sort` is stable in
 * every engine we ship to — rather than being flung to one end on a value we do not have: a legacy
 * pseudo-pack (lib/storySteps.js's `storyPacks`) has no date at all and is still a pack.
 *
 * @param {Array<object>} packs the ongoing's packs
 * @returns {Array<object>} a new array, newest first
 */
export function packsNewestFirst(packs) {
  const at = (p) => {
    const t = Date.parse(p?.createdDate ?? '')
    return Number.isNaN(t) ? null : t
  }
  return (packs || []).filter(Boolean).slice().sort((a, b) => {
    const x = at(a)
    const y = at(b)
    // Undated packs do not move: only two packs that BOTH state when they were made can be ordered
    // by it, and anything else would be sorting on a guess.
    if (x === null || y === null) return 0
    return y - x
  })
}

export default isPackSelected
